import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import { bodyText, colours } from '../player/playerStyles'
import Feedback from './Feedback'
import useBlockState, { shuffle } from './useBlockState'

const controlSx = { color: colours.border, '&.Mui-checked': { color: colours.accent } }

// A practice question: unscored, with feedback on every option chosen, and
// the learner can change the answer and check again.
const KnowledgeCheck = ({ block, initialState, onStateChange }) => {
  const [options] = useState(() => shuffle(block.options))
  const [state, update] = useBlockState({ selected: [], submitted: false, ...initialState }, onStateChange)
  const [prompted, setPrompted] = useState(false)
  const selected = state.selected ?? []
  const Control = block.multi ? Checkbox : Radio

  const choose = (optionId) => {
    const next = block.multi
      ? selected.includes(optionId)
        ? selected.filter((id) => id !== optionId)
        : [...selected, optionId]
      : [optionId]
    setPrompted(false)
    update({ ...state, selected: next, submitted: false })
  }

  const check = () => {
    if (!selected.length) {
      setPrompted(true)
      return
    }
    update({ ...state, submitted: true })
  }

  const chosen = options.filter((option) => selected.includes(option.id))
  const missed = options.filter((option) => option.correct && !selected.includes(option.id))
  const allRight = chosen.every((option) => option.correct) && missed.length === 0

  return (
    <Box sx={{ maxWidth: '74ch' }}>
      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <FormLabel
          component="legend"
          sx={{ ...bodyText, fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.5, color: `${colours.text} !important`, mb: 1, p: 0 }}
        >
          {block.question}
        </FormLabel>
        <Typography sx={{ fontSize: '1rem', color: colours.muted, mb: 1 }}>
          {block.multi ? 'Select all that apply.' : 'Select one answer.'} This question does not count towards your result.
        </Typography>
        <Box>
          {options.map((option) => (
            <FormControlLabel
              key={option.id}
              control={
                <Control
                  name={block.id}
                  checked={selected.includes(option.id)}
                  onChange={() => choose(option.id)}
                  sx={controlSx}
                />
              }
              label={option.text}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                minHeight: 44,
                mx: 0,
                mb: 1,
                p: 0.5,
                border: `1px solid ${selected.includes(option.id) ? colours.accent : colours.hairline}`,
                borderRadius: 1.5,
                bgcolor: selected.includes(option.id) ? colours.accentSoft : '#FFFFFF',
                '& .MuiFormControlLabel-label': { fontSize: '1.0625rem', lineHeight: 1.5, py: '9px', pr: 1 },
              }}
            />
          ))}
        </Box>
      </FormControl>

      <Button variant="contained" onClick={check} sx={{ mt: 1 }}>
        Check answer
      </Button>

      <Box aria-live="polite" sx={{ mt: 2 }}>
        {prompted && !selected.length && (
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: colours.bad }}>Choose an answer first.</Typography>
        )}
        {state.submitted && selected.length > 0 && (
          <>
            <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 1 }}>
              {allRight ? 'That is right' : 'Not quite right'}
            </Typography>
            {chosen.map((option) => (
              <Feedback
                key={option.id}
                tone={option.correct ? 'good' : 'bad'}
                label={`${option.correct ? 'Correct' : 'Not correct'}: you chose "${option.text}"`}
                sx={{ mb: 1 }}
              >
                {option.feedback}
              </Feedback>
            ))}
            {missed.map((option) => (
              <Feedback
                key={option.id}
                tone="info"
                label={`${block.multi ? 'A correct answer you did not choose' : 'The correct answer'}: "${option.text}"`}
                sx={{ mb: 1 }}
              >
                {option.feedback}
              </Feedback>
            ))}
            {!allRight && (
              <Typography sx={{ fontSize: '1rem', color: colours.muted }}>
                You can change your answer and check again, or carry on.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default KnowledgeCheck
